<template>
  <div class="calendar brand-card-lg">
    <div class="space-x-6 flex items-center">
      <p class="inline title ml-4 mb-6 space-x-2 text-3xl">
        <span class="font-bold">April</span>
        <span class="font-medium">2023</span>
      </p>
    </div>
    <div class="grid grid-cols-7 gap-x-2 gap-y-3">
      <div v-for="day in daysOfWeek" :key="day" class="font-bold text-center p-2">
        {{ day }}
      </div>
      <template v-for="week in weeks">
        <div
          v-for="day in week.days"
          :key="day.date.getDate() + day.isEmpty"
          class="h-28 group transition-colors duration-150"
          :class="[
            'text-center rounded-lg flex flex-col flex-grow justify-between items-center relative',
            day.isEmpty ? 'bg-gray-50' : 'border border-gray-300 hover:bg-indigo-50 hover:border-indigo-400 cursor-pointer',
            day.isToday ? 'bg-indigo-50 border-indigo-400 border-2 font-semibold text-brand-blue' : '',
          ]"
        >
          <div v-if="!day.isEmpty" class="relative w-full h-28" :id="`container-${day.date.getDate()}`">
            <svg class="z-20 h-28 w-full" :id="`svg-${day.date.getDate()}`" viewbox="0 0 140 112" preserveAspectRatio="xMidYMid meet" />
            <!-- <canvas class="w-full h-auto" width="400" height="300" :id="`canvas-${day.date.getDate()}`" /> -->
          </div>
          <div class="absolute bottom-2 right-4 text-xs font-semibold text-gray-800">
            {{ day.isEmpty ? '' : day.date.getDate() }}
          </div>
          <div
            v-if="dailySummary && dailySummary[day.date.getDate() - 1] && !day.isEmpty"
            class="absolute bottom-2 left-4 text-xs font-semibold text-gray-800"
          >
            {{ dailySummary[day.date.getDate() - 1].display }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import statsService from '@/api/stats.service';
import { LOAD_STATE } from '@/store/shared';
import * as d3 from 'd3';
import { formatSmallMoney } from '@/helpers/jsUtils';

const cellHeight = 112;
const halfCellHeight = cellHeight / 2;

export default {
  props: {
    month: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      stats: null,
      loadState: LOAD_STATE.UNLOADED,
    };
  },
  async created() {
    const startDate = new Date(`${this.month}-01`);
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
    try {
      this.loadState = LOAD_STATE.LOADING;
      this.stats = await statsService.invoiceCount({ filter: { startDate, endDate }, breakdown: ['invoiceHour'] });
      this.drawStreamlines();
      this.loadState = LOAD_STATE.LOADED;
    } catch (error) {
      this.loadState = LOAD_STATE.ERROR;
    }
  },
  computed: {
    hourlyStats() {
      if (!this.stats) return {};
      const statsPerDay = {};
      this.stats.forEach((stat) => {
        // trim off the last 3 characters
        const day = stat.invoiceHour.substring(0, stat.invoiceHour.length - 3);
        const hour = +stat.invoiceHour.substring(stat.invoiceHour.length - 2);
        if (statsPerDay[day] === undefined) {
          statsPerDay[day] = [];
        }
        statsPerDay[day][hour] = stat;
      });
      Object.keys(statsPerDay).forEach((day) => {
        statsPerDay[day].sort((a, b) => d3.ascending(a.invoiceHour, b.invoiceHour));
      });
      return statsPerDay;
    },
    dailySummary() {
      if (!this.hourlyStats) return null;
      const dailyAggregation = {};
      const values = ['amountPaid', 'count'];

      Object.keys(this.hourlyStats).forEach((day) => {
        dailyAggregation[day] = {};
        values.forEach((value) => {
          dailyAggregation[day][value] = this.hourlyStats[day].reduce((acc, stat) => acc + stat[value], 0);
        });
      });

      console.log(dailyAggregation);
      return Object.keys(dailyAggregation).map((day) => ({
        day,
        ...dailyAggregation[day],
        display: dailyAggregation[day] ? formatSmallMoney(dailyAggregation[day].amountPaid / 100) : '',
      }));
    },
    loading() {
      return this.loadState !== LOAD_STATE.LOADED;
    },
    maxValue() {
      return Math.max(...this.stats.map((stat) => stat.amountPaid));
    },
    areaGenerator() {
      return (
        d3
          .area()
          .x((d, i) => (i * 140) / 24)
          // balance it equally above and below the x axis
          .y0((d) => (d ? halfCellHeight - (d.amountPaid / this.maxValue) * halfCellHeight : halfCellHeight))
          .y1((d) => (d ? halfCellHeight + (d.amountPaid / this.maxValue) * halfCellHeight : halfCellHeight))
          .curve(d3.curveBasis)
      );
    },
    weeks() {
      const weeks = [];
      const inputDate = new Date(`${this.month}-01`);
      const month = inputDate.getMonth();
      const year = inputDate.getFullYear();
      const currentDate = new Date(year, month, 1);
      let weekNumber = 0;

      while (currentDate.getMonth() === month) {
        if (!weeks[weekNumber]) {
          weeks[weekNumber] = {
            id: weekNumber,
            days: [],
          };
        }

        for (let i = 0; i < 7; i++) {
          if (currentDate.getDay() === i && currentDate.getMonth() === month) {
            weeks[weekNumber].days.push({
              date: new Date(currentDate),
              isToday: this.isToday(currentDate),
              isEmpty: false,
            });
            currentDate.setDate(currentDate.getDate() + 1);
          } else {
            weeks[weekNumber].days.push({
              date: new Date(currentDate),
              isToday: false,
              isEmpty: true,
            });
          }
        }
        weekNumber++;
      }

      return weeks;
    },
  },
  methods: {
    isToday(date) {
      const today = new Date();
      return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    },
    drawDateStreamline(date) {
      if (!date.getDate) return;
      const dateNumber = date.getDate();
      let paddedDateNumber = dateNumber;
      // add padding 0s to the date
      if (dateNumber < 10) {
        paddedDateNumber = `0${dateNumber}`;
      }
      const fullDate = `${this.month}-${paddedDateNumber}`;
      const dateStats = this.hourlyStats[fullDate];

      if (!dateStats) {
        return;
      }

      const svgId = `svg-${dateNumber}`;
      const svg = d3.select(`#${svgId}`);

      // add def for gradient fill for area
      const defs = svg.append('defs');

      const gradient = defs
        .append('linearGradient')
        .attr('id', `gradient-${dateNumber}`)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      gradient.append('stop').attr('offset', '0%').attr('stop-color', '#504dd9').attr('stop-opacity', 0.5);

      gradient.append('stop').attr('offset', '50%').attr('stop-color', '#c7d2fe').attr('stop-opacity', 0.8);

      gradient.append('stop').attr('offset', '50%').attr('stop-color', '#c7d2fe').attr('stop-opacity', 0.8);

      gradient.append('stop').attr('offset', '100%').attr('stop-color', '#504dd9').attr('stop-opacity', 0.5);

      // draw the streamgraph onto the SVG
      svg
        .append('path')
        .datum(dateStats)
        // .attr('fill', '#2010a0')
        .attr('fill', `url(#gradient-${dateNumber})`)
        .attr('stroke', '#504dd9')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.2)
        // .attr('fill', 'rgba(0, 0, 0, 0.2)')
        .attr('d', this.areaGenerator)
        .attr('mix-blend-mode', 'difference');

      // svg
      //   .append('g')
      //   .attr('transform', `translate(5, 0)`)
      //   .append('path')
      //   .datum(dateStats)
      //   .attr('fill', 'rgba(0, 0, 0, 0.1)')
      //   .attr('d', this.areaGenerator)
      //   .attr('mix-blend-mode', 'difference');
    },
    drawStreamlines() {
      // Draw the canvas for each date
      this.weeks.forEach((week) => {
        week.days.forEach((day) => {
          if (!day.isEmpty) {
            this.$nextTick(() => {
              this.drawDateStreamline(day.date);
            });
          }
        });
      });
    },
  },
};
</script>
